/**
 * Generate an unique ID.
 * Implementation taken from Firestore: https://github.com/firebase/firebase-js-sdk/blob/5ad7ff2ae955c297556223e6cb3ad9d4b897f664/packages/firestore/src/util/misc.ts#L28
 *
 * Given `fromBytes` (e.g. a hash), the ID is derived from them instead of random bytes, so the same
 * bytes always give the same ID. Some bytes are skipped: 40 bytes are enough in practice.
 */
export function genID(fromBytes?: Uint8Array): string {
    // Alphanumeric characters
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    // The largest byte value that is a multiple of `char.length`.
    const maxMultiple = Math.floor(256 / chars.length) * chars.length;

    let autoId = '';
    const targetLength = 20;
    while (autoId.length < targetLength) {
        const bytes = fromBytes ?? randomBytes(40);
        for (let i = 0; i < bytes.length; ++i) {
            // Only accept values that are [0, maxMultiple), this ensures they can
            // be evenly mapped to indices of `chars` via a modulo operation.
            if (autoId.length < targetLength && bytes[i] < maxMultiple) {
                autoId += chars.charAt(bytes[i] % chars.length);
            }
        }
        if (fromBytes && autoId.length < targetLength) {
            throw new Error(`Not enough bytes to generate an ID of ${targetLength} characters`);
        }
    }

    return autoId;
}

/**
 * Generates `nBytes` of random bytes.
 */
export function randomBytes(nBytes: number): Uint8Array {
    if (typeof crypto.getRandomValues === 'function') {
        const bytes = new Uint8Array(nBytes);
        crypto.getRandomValues(bytes);
        return bytes;
    }

    throw new Error('Unable to generate random bytes in this environment.');
}

const ESCAPING: Array<[[RegExp, string], [RegExp, string]]> = [
    [
        [/%/g, '%25'],
        [/%25/g, '%'],
    ],
    [
        [/\./g, '%2E'],
        [/%2E/g, '.'],
    ],
    [
        [/#/g, '%23'],
        [/%23/g, '#'],
    ],
    [
        [/\$/g, '%24'],
        [/%24/g, '$'],
    ],
    [
        [/\//g, '%2F'],
        [/%2F/g, '/'],
    ],
    [
        [/\[/g, '%5B'],
        [/%5B/g, '['],
    ],
    [
        [/\]/g, '%5D'],
        [/%5D/g, ']'],
    ],
    [
        [/\n/g, '%0A'],
        [/%0A/g, '\n'],
    ],

    // Firestore keys cannot start with 2 underscores
    [
        [/^__/g, '%5F%5F'],
        [/^%5F%5F/g, '__'],
    ],
];

/**
 * Escape an URI.
 * Example: to be compatible with Firestore and be used as a key.
 */
export function escapeKey(key: string): string {
    return ESCAPING.reduce((result, pattern) => result.replace(pattern[0][0], pattern[0][1]), key);
}

/**
 * Unescape an URI.
 */
export function unescapeKey(key: string): string {
    return [...ESCAPING]
        .reverse()
        .reduce((result, pattern) => result.replace(pattern[1][0], pattern[1][1]), key);
}

/**
 * Validate that a string is a valid Firestore key.
 * https://firebase.google.com/docs/firestore/quotas#collections_documents_and_fields
 *
 * - Must be valid UTF-8 characters
 * - Must be no longer than 1,500 bytes
 * - Cannot contain a forward slash (/)
 * - Cannot solely consist of a single period (.) or double periods (..)
 * - Cannot match the regular expression __.*__
 */
export function validateFirestoreKey(key: string): boolean {
    return (
        key.length > 0 &&
        key.length <= 1500 &&
        !key.includes('/') &&
        !key.match(/^\.+$/) &&
        !key.match(/^__.*__$/)
    );
}
